import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import { appColors } from '../../Config/Theme';
import {
  createManagedUser,
  getUserManagementErrorMessage,
  searchEmployeeDirectory,
} from '../../Services/UserManagementService';
import {
  EmployeeDirectoryEmployee,
  ManagedUser,
  UserManagementMetadata,
} from '../../Types/UserManagement';
import RoleAssignmentEditor, {
  buildRoleAssignmentInputs,
  createRoleAssignmentDrafts,
  RoleAssignmentDrafts,
} from './RoleAssignmentEditor';

interface AddManagedUserDialogProps {
  open: boolean;
  metadata: UserManagementMetadata;
  onClose: () => void;
  onCreated: (user: ManagedUser) => void;
  onManageExisting: (appUserId: string) => void;
  initialEmployee?: EmployeeDirectoryEmployee | null;
}

const AddManagedUserDialog: React.FC<AddManagedUserDialogProps> = ({
  open,
  metadata,
  onClose,
  onCreated,
  onManageExisting,
  initialEmployee = null,
}) => {
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [employees, setEmployees] = useState<EmployeeDirectoryEmployee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeDirectoryEmployee | null>(null);
  const [drafts, setDrafts] = useState<RoleAssignmentDrafts>({});
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setEmployeeSearch(initialEmployee?.displayName ?? '');
    setEmployees(initialEmployee?.status === 'A' ? [initialEmployee] : []);
    setSelectedEmployee(initialEmployee?.status === 'A' ? initialEmployee : null);
    setDrafts(createRoleAssignmentDrafts(metadata.roles));
    setSearching(false);
    setSaving(false);
    setError(null);
  }, [initialEmployee, open, metadata.roles]);

  useEffect(() => {
    if (!open || employeeSearch.trim().length < 2) {
      if (!initialEmployee) setEmployees([]);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setSearching(true);
      try {
        const results = await searchEmployeeDirectory(employeeSearch, {
          limit: 20,
          includeProvisioned: true,
        });
        if (!cancelled) {
          setEmployees(results.filter((employee) => employee.status === 'A'));
          setError(null);
        }
      } catch (searchError) {
        if (!cancelled) {
          setEmployees([]);
          setError(getUserManagementErrorMessage(
            searchError,
            'Unable to search the employee directory.',
          ));
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [employeeSearch, initialEmployee, open]);

  const assignments = useMemo(
    () => buildRoleAssignmentInputs(metadata.roles, drafts),
    [drafts, metadata.roles],
  );

  const hasIncompleteLocationRole = metadata.roles.some((role) => {
    const draft = drafts[role.appRoleId];
    return role.canAssign &&
      draft?.selected &&
      !role.requiresGlobalScope &&
      draft.scope === 'locations' &&
      draft.locationIds.length === 0;
  });

  const canSubmit =
    selectedEmployee?.canProvision === true &&
    !hasIncompleteLocationRole &&
    (metadata.capabilities.canManageGlobal || assignments.length > 0);

  const handleSubmit = async () => {
    if (!canSubmit || !selectedEmployee) return;
    setSaving(true);
    setError(null);
    try {
      const user = await createManagedUser({
        employeeId: selectedEmployee.employeeId,
        roleAssignments: assignments,
      });
      onCreated(user);
    } catch (submissionError) {
      setError(getUserManagementErrorMessage(submissionError, 'Unable to add this EEAS user.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pb: 1 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: '10px',
            bgcolor: 'rgba(46, 139, 132, 0.12)',
            color: appColors.primary.main,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <PersonAddAltOutlinedIcon />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Add EEAS User
          </Typography>
          <Typography variant="caption" sx={{ color: appColors.text.caption }}>
            Grant application access to an active Exponent employee
          </Typography>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2.5 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Alert severity="info" variant="outlined">
          Search the Exponent employee directory. EEAS re-reads the selected employee and uses the
          authoritative HR identity record when access is created.
        </Alert>
        <Box>
          <Typography
            component="label"
            htmlFor="eeas-employee-search"
            variant="body2"
            sx={{ display: 'block', fontWeight: 600, color: appColors.text.heading, mb: 0.5 }}
          >
            Employee <Box component="span" sx={{ color: appColors.status.error }}>*</Box>
          </Typography>
          <Autocomplete
            options={employees}
            value={selectedEmployee}
            inputValue={employeeSearch}
            loading={searching}
            filterOptions={(options) => options}
            getOptionLabel={(employee) => employee.displayName}
            isOptionEqualToValue={(option, value) => option.employeeId === value.employeeId}
            onInputChange={(_, value, reason) => {
              setEmployeeSearch(value);
              if (reason === 'input') setSelectedEmployee(null);
            }}
            onChange={(_, value) => {
              setSelectedEmployee(value);
              if (value?.isProvisioned && value.appUserId) {
                onManageExisting(value.appUserId);
              }
            }}
            noOptionsText={
              employeeSearch.trim().length < 2
                ? 'Enter at least two characters'
                : 'No employees found'
            }
            renderOption={(props, employee) => (
              <Box component="li" {...props} key={employee.employeeId}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {employee.displayName}
                  </Typography>
                  <Typography variant="caption" sx={{ color: appColors.text.caption }}>
                  {employee.upn || employee.businessEmail || 'No email'} · Employee {employee.employeeId}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    ml: 1,
                    fontWeight: 700,
                    color: employee.isProvisioned
                      ? appColors.primary.main
                      : appColors.status.success,
                  }}
                >
                  {employee.isProvisioned
                    ? 'Manage roles'
                    : 'Available to add'}
                </Typography>
              </Box>
            )}
            getOptionDisabled={(employee) => !employee.isProvisioned && !employee.canProvision}
            renderInput={(params) => {
              const { slotProps: paramSlotProps, ...restParams } = params;
              const inputSlotProps = (paramSlotProps?.input ?? {}) as Record<string, any>;
              return (
                <TextField
                  {...restParams}
                  id="eeas-employee-search"
                  placeholder="Search name, employee ID, UPN, or email"
                  required
                  autoFocus
                  slotProps={{
                    ...paramSlotProps,
                    input: {
                      ...inputSlotProps,
                      endAdornment: (
                        <>
                          {searching ? <CircularProgress color="inherit" size={18} /> : null}
                          {inputSlotProps.endAdornment}
                        </>
                      ),
                    },
                    htmlInput: {
                      ...paramSlotProps?.htmlInput,
                      'aria-label': 'Employee',
                      'aria-required': true,
                    },
                  }}
                />
              );
            }}
          />
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Initial role assignments
          </Typography>
          <Typography variant="caption" sx={{ color: appColors.text.caption, display: 'block', mb: 1.25 }}>
            Global and location-scoped assignments can be combined.
          </Typography>
          <RoleAssignmentEditor
            roles={metadata.roles}
            locations={metadata.locations}
            capabilities={metadata.capabilities}
            drafts={drafts}
            onChange={setDrafts}
            disabled={saving}
          />
          {hasIncompleteLocationRole && (
            <Typography variant="caption" sx={{ color: appColors.status.error, mt: 1, display: 'block' }}>
              Select at least one location for each location-scoped role.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {saving ? 'Adding…' : 'Add User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddManagedUserDialog;
